import type {z as zod} from "zod";
import {createDefaultTableComparisonConfig} from "~/domainModels/reports/tableComparison";
import {WidgetLoadingState, type Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {StoredWidget} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {storedWidgetZodValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJsonValidators";

export function hydrateStoredWidgetForFrontend(widget: StoredWidget): Widget {
    switch (widget.type) {
        case WidgetType.multiBarGraph:
        case WidgetType.stackedBarGraph:
        case WidgetType.horizontalBarGraph:
        case WidgetType.lineGraph:
        case WidgetType.areaGraph:
        case WidgetType.comboChart: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notStarted,
                startDateJs: null,
                endDateJs: null,
                xAxisKey: null,
                xAxisTitle: null,
                yAxisTitle: null,
                data: null,
                errorCode: null,
                columnMetadata: null,
                columnAttributeIds: null,
            };
        }
        case WidgetType.table: {
            return {
                ...widget,
                comparison: widget.comparison ?? createDefaultTableComparisonConfig(),
                loadingState: WidgetLoadingState.notStarted,
                startDateJs: null,
                endDateJs: null,
                data: null,
                errorCode: null,
                columnMetadata: null,
                columnAttributeIds: null,
                comparisonData: null,
                comparisonColumnMetadata: null,
                comparisonColumnAttributeIds: null,
                comparisonLoadingState: WidgetLoadingState.notStarted,
                comparisonErrorCode: null,
            };
        }
        case WidgetType.numberTile: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notStarted,
                startDateJs: null,
                endDateJs: null,
                aggregatedValue: null,
                errorCode: null,
                columnMetadata: null,
                columnAttributeIds: null,
            };
        }
        case WidgetType.pieChart:
        case WidgetType.donutChart: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notStarted,
                startDateJs: null,
                endDateJs: null,
                data: null,
                errorCode: null,
                columnMetadata: null,
                columnAttributeIds: null,
            };
        }
        case WidgetType.progressBar:
        case WidgetType.gauge: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notStarted,
                startDateJs: null,
                endDateJs: null,
                progressValue: null,
                errorCode: null,
            };
        }
        case WidgetType.text: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notRequired,
            };
        }
        case WidgetType.image: {
            return {
                ...widget,
                loadingState: WidgetLoadingState.notRequired,
            };
        }
        default: {
            const exhaustiveCheck: never = widget;
            return exhaustiveCheck;
        }
    }
}

export const widgetZodValidator: zod.ZodType<Widget> = storedWidgetZodValidator.transform((storedWidget) => hydrateStoredWidgetForFrontend(storedWidget));
